import { createContext, useState, useCallback, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

export type SnackbarVariant = 'default' | 'error' | 'success' | 'warning' | 'info';

export interface SnackbarOptions {
  variant?: SnackbarVariant;
  autoHideDuration?: number;
}

interface SnackbarContextType {
  showSnackbar: (message: string, options?: SnackbarOptions) => void;
}

export const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

interface SnackbarProviderProps {
  children: ReactNode;
}

interface SnackbarState {
  open: boolean;
  message: string;
  variant: SnackbarVariant;
  autoHideDuration: number;
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    variant: 'default',
    autoHideDuration: 6000,
  });

  const showSnackbar = useCallback((message: string, options?: SnackbarOptions) => {
    setSnackbar({
      open: true,
      message,
      variant: options?.variant || 'default',
      autoHideDuration: options?.autoHideDuration || 6000,
    });
  }, []);

  const handleClose = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const getSeverity = (variant: SnackbarVariant): AlertColor | undefined => {
    if (variant === 'default') return undefined;
    return variant as AlertColor;
  };

  const severity = getSeverity(snackbar.variant);

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {severity ? (
          <Alert onClose={handleClose} severity={severity} variant="filled" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : (
          <Alert
            onClose={handleClose}
            variant="filled"
            sx={{ width: '100%', backgroundColor: 'grey.800', color: 'white' }}
          >
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </SnackbarContext.Provider>
  );
}
